import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import './ConfirmEmail.css';

const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('Confirmando seu email...');
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [emailFromToken, setEmailFromToken] = useState('');

  // Função para reenviar email de confirmação
  const handleResendEmail = async () => {
    if (!emailFromToken) {
      setResendMessage('Não foi possível identificar o email. Tente novamente.');
      return;
    }

    setIsResending(true);
    setResendMessage('');

    try {
      const response = await apiService.post<{
        success: boolean;
        message?: string;
      }>('RESEND_CONFIRMATION', {
        email: emailFromToken
      });

      if (response.success) {
        setResendMessage('Email de confirmação reenviado com sucesso! Verifique sua caixa de entrada.');
      } else {
        setResendMessage(response.message || 'Erro ao reenviar email. Tente novamente.');
      }
    } catch (error: any) {
      console.error('Erro ao reenviar email:', error);
      setResendMessage('Erro ao reenviar email. Tente novamente mais tarde.');
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    const confirmEmail = async () => {
      let token = searchParams.get('token');

      // Decodificar o token se estiver codificado para URL
      if (token) {
        token = decodeURIComponent(token);

        // Extrair email do token JWT (payload está no meio do token, separado por pontos)
        try {
          const payload = token.split('.')[1];
          const decodedPayload = JSON.parse(atob(payload));
          if (decodedPayload.email) {
            setEmailFromToken(decodedPayload.email);
          }
        } catch (error) {
          console.error('Erro ao decodificar token:', error);
        }
      }

      // Verificar se o token está presente
      if (!token) {
        setMessage('Token de confirmação não encontrado.');
        setIsError(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiService.post<{
          success: boolean;
          message?: string;
          alreadyConfirmed?: boolean;
        }>('CONFIRM_EMAIL', {
          token
        });

        if (response.success) {
          setMessage(response.message || 'Email confirmado com sucesso! Redirecionando para o login...');
          setIsError(false);
          setIsSuccess(true);

          // Redirecionar para login após 3 segundos
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setMessage(response.message || 'Erro ao confirmar email. Tente novamente.');
          setIsError(true);
        }
      } catch (error: any) {
        console.error('Erro ao confirmar email:', error);

        let errorMessage = 'Erro ao confirmar email. Tente novamente.';

        if (error.message && error.message.includes('400')) {
          errorMessage = 'Token inválido ou expirado. Entre em contato com o suporte.';
        } else if (error.message && error.message.includes('404')) {
          errorMessage = 'Usuário não encontrado. Entre em contato com o suporte.';
        } else if (error.message) {
          errorMessage = error.message;
        }

        setMessage(errorMessage);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    confirmEmail();
  }, [searchParams, navigate]);

  return (
    <div className="confirm-email-page">
      <div className="confirm-email-container">
        <div className="logo-section">
          <div className="logo">
            <img
              src="https://a.imagem.app/3bTgpi.png"
              alt="Logo ROBOT.IA"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                if (target.parentElement) {
                  target.parentElement.innerHTML = 'ROBOT.IA';
                }
              }}
            />
          </div>
          <h1 className="logo-text">ROBOT.IA</h1>
          <p className="logo-subtitle">Confirmação de Email</p>
        </div>

        {isLoading ? (
          <div className="confirm-email-loading">
            <LoadingSpinner />
            <p>{message}</p>
          </div>
        ) : (
          <>
            <div className={`confirm-email-message ${isError ? 'error' : 'success'}`}>
              {message}
            </div>

            {isSuccess && (
              <div className="confirm-email-success-details">
                <div className="success-icon">✓</div>
                <p>Seu email foi confirmado com sucesso!</p>
                <p>Você será redirecionado para o login em instantes...</p>
              </div>
            )}

            {isError && (
              <div className="confirm-email-actions">
                {emailFromToken && (
                  <>
                    <div className="resend-email-section">
                      <p>Não recebeu o email ou o link expirou?</p>
                      <button
                        type="button"
                        className="resend-button"
                        onClick={handleResendEmail}
                        disabled={isResending}
                      >
                        {isResending ? 'Reenviando...' : 'Reenviar Email de Confirmação'}
                      </button>
                      {resendMessage && (
                        <div className={`resend-message ${resendMessage.includes('sucesso') ? 'success' : 'error'}`}>
                          {resendMessage}
                        </div>
                      )}
                    </div>
                    <div className="divider">ou</div>
                  </>
                )}
                <div className="confirm-email-back-button">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                  >
                    Voltar para o Login
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmail;
